import {
    AlertTriangle,
    Check,
    ChevronDown,
    ChevronUp,
    Minus,
    Plus,
    Trash2,
    Truck,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../Redux/hooks";
import { RootState } from "../../../../../store";
import {
    removeFromCart,
    selectDeliveryOption,
    updateCartItem,
} from "../../../../../Redux/custmoer_cart_service/slice";
import {
    CartType,
    ProductType,
} from "../../../../../Redux/custmoer_cart_service/types";
import { checkPdpDelivery } from "../../../../../Redux/shipping-apis/slice";
import "./CheckoutReviewOrder.css";

/** Shared Amazon-style undeliverable notice */
export const UndeliverableBanner = ({ pincode }: { pincode: string }) => (
    <div
        className="checkout-undeliverable-banner"
        style={{
            margin: "8px 0",
            padding: "10px 14px",
            background: "#fff5f5",
            border: "1px solid #f5c2c7",
            borderRadius: "8px",
        }}
    >
        <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#dc3545", fontWeight: 700, fontSize: "13px", marginBottom: "3px" }}>
            <AlertTriangle size={15} />
            <span>This order cannot be delivered to {pincode}</span>
        </div>
        <p style={{ color: "#6c757d", fontSize: "11.5px", margin: 0, lineHeight: 1.4 }}>
            Select a different delivery address to continue. Payment is disabled until a deliverable address is chosen.
        </p>
    </div>
);

const CheckoutReviewOrder = () => {
    const dispatch = useAppDispatch();
    const { cart } = useAppSelector((state: RootState) => state.customerCart);
    const { addresses } = useAppSelector((state: RootState) => state.customerAddress);
    const { pdpDelivery, pdpDeliveryLoading } = useAppSelector(
        (state: RootState) => state.shipping
    );

    const [openCourierCartId, setOpenCourierCartId] = useState<string | null>(null);

    const sellerId = cart?.items?.find((i) => i.sellerId)?.sellerId ?? null;
    const selectedAddress = addresses.find(
        (a) => a._id === cart?.selectedAddressId
    );

    // Trigger delivery serviceability check when address pincode changes
    useEffect(() => {
        if (selectedAddress?.pincode) {
            dispatch(
                checkPdpDelivery({
                    sellerId: sellerId || "PLATFORM",
                    customerPincode: selectedAddress.pincode,
                })
            );
        }
    }, [dispatch, sellerId, selectedAddress?._id, selectedAddress?.pincode]);

    const pinMatches =
        !!pdpDelivery &&
        !!selectedAddress?.pincode &&
        pdpDelivery.customerPincode === selectedAddress.pincode;

    const standardOpt = pdpDelivery?.deliveryOptions?.find((o) => o.id === "standard");
    const sameDayOpt = pdpDelivery?.deliveryOptions?.find((o) => o.id === "same_day");
    const hlCheapest = useMemo(() => {
        const opts = (pdpDelivery?.hyperlocal?.options ?? []).filter((o) => o.isHyperlocal);
        return [...opts].sort((a, b) => a.rate - b.rate)[0];
    }, [pdpDelivery?.hyperlocal?.options]);

    const standardAvailable =
        pinMatches &&
        !pdpDeliveryLoading &&
        (pdpDelivery?.delhivery?.serviceable === true || standardOpt?.available === true);

    const sameDayAvailable =
        pinMatches &&
        !pdpDeliveryLoading &&
        (pdpDelivery?.hyperlocal?.serviceable === true || sameDayOpt?.available === true) &&
        !!hlCheapest;

    const undeliverable =
        !!selectedAddress?.pincode &&
        pinMatches &&
        !pdpDeliveryLoading &&
        !standardAvailable &&
        !sameDayAvailable;

    const sameDayCost = hlCheapest?.rate ?? sameDayOpt?.price ?? 0;
    const standardEstimatedDays = Math.max(
        0,
        Number(pdpDelivery?.delhivery?.estimatedDays ?? 2)
    );

    const handleRemoveItem = (itemId: string) => {
        dispatch(removeFromCart({ itemId }));
    };

    const handleQuantityUpdate = (itemId: string, newQuantity: number) => {
        if (newQuantity < 1) {
            handleRemoveItem(itemId);
        } else {
            dispatch(
                updateCartItem({
                    itemId,
                    data: { quantity: newQuantity },
                    cartType: CartType.ECOMMERCE,
                })
            );
        }
    };

    const handleSelectDelivery = (
        name: string,
        price: number,
        optionId: string,
        courierCompanyId?: number,
        estimatedDays?: number
    ) => {
        const days =
            estimatedDays != null
                ? estimatedDays
                : optionId === "sameday"
                    ? 0
                    : optionId === "exp"
                        ? 5
                        : standardEstimatedDays;
        dispatch(
            selectDeliveryOption({
                data: {
                    optionId,
                    name,
                    price,
                    courierCompanyId,
                    estimatedDays: String(days),
                    isFree: price === 0,
                },
                cartType: CartType.ECOMMERCE,
            })
        );
    };

    // Auto-fallback effect: falls back to standard if same-day drops
    useEffect(() => {
        if (pdpDeliveryLoading || !pinMatches) return;
        if (undeliverable && cart?.deliveryOptionId) return;

        if (cart?.deliveryOptionId === "sameday" && !sameDayAvailable) {
            if (standardAvailable) {
                handleSelectDelivery(
                    "Standard Delivery",
                    Number(standardOpt?.price ?? 0),
                    "std",
                    undefined,
                    standardEstimatedDays
                );
            }
        }

        if (
            (!cart?.deliveryOptionId || cart.deliveryOptionId === "sameday") &&
            standardAvailable &&
            cart?.deliveryOptionId !== "std"
        ) {
            if (!cart?.deliveryOptionId) {
                handleSelectDelivery(
                    "Standard Delivery",
                    Number(standardOpt?.price ?? 0),
                    "std",
                    undefined,
                    standardEstimatedDays
                );
            }
        }
    }, [
        pdpDeliveryLoading,
        pinMatches,
        undeliverable,
        sameDayAvailable,
        standardAvailable,
        cart?.deliveryOptionId,
    ]);

    const hasPhysicalItem =
        cart?.items.some((item) => item.productType === ProductType.PHYSICAL) ?? false;

    const currentOptionId = cart?.deliveryOptionId || "std";

    if (!cart || cart.items.length === 0) {
        return (
            <section className="checkout-review-order">
                <div className="checkout-review-header">
                    <div>
                        <span>ORDER REVIEW</span>
                        <h2>Review Order</h2>
                    </div>
                    <Truck size={18} strokeWidth={1.8} />
                </div>
                <div style={{ padding: "24px", textAlign: "center", color: "#667085" }}>
                    Your cart is currently empty.
                </div>
            </section>
        );
    }

    const hyperlocalCourierOptions = (pdpDelivery?.hyperlocal?.options ?? []).filter((o) => o.isHyperlocal);

    return (
        <section className="checkout-review-order">
            <div className="checkout-review-header">
                <div>
                    <span>ORDER REVIEW</span>
                    <h2>Review Order</h2>
                </div>
                <Truck size={18} strokeWidth={1.8} />
            </div>

            <div className="checkout-review-list">
                {cart.items.map((item) => {
                    return (
                        <article className="checkout-review-product-card" key={item.id}>
                            <div className="checkout-review-product">
                                <div className="checkout-review-image-wrap">
                                    <Link to={`/DetailPage/${item.productId}`}>
                                        <img
                                            src={item.productImage || "/assets/img/placeholder.png"}
                                            alt={item.productName}
                                            className="checkout-review-image"
                                        />
                                    </Link>
                                </div>

                                <div className="checkout-review-product-content">
                                    <Link
                                        to={`/DetailPage/${item.productId}`}
                                        style={{ textDecoration: "none", color: "inherit" }}
                                    >
                                        <h3>{item.productName}</h3>
                                    </Link>

                                    <span
                                        className="checkout-review-stock"
                                        style={{
                                            color: undeliverable ? "#dc3545" : "#228b45",
                                            fontWeight: 700,
                                        }}
                                    >
                                        {undeliverable
                                            ? "Unavailable for selected address"
                                            : "In stock"}
                                    </span>

                                    <p className="checkout-review-seller">
                                        <strong>Sold By:</strong> {item.sellerName || "Salar Retail"}
                                    </p>

                                    <div className="checkout-review-meta">
                                        <span>
                                            Price:{" "}
                                            <strong style={{ color: "#173c72" }}>
                                                ₹{(item.finalPrice ?? item.unitPrice ?? 0).toLocaleString("en-IN")}
                                            </strong>
                                        </span>
                                    </div>

                                    <div className="checkout-review-quantity">
                                        <button
                                            type="button"
                                            onClick={() => handleQuantityUpdate(item.id, item.quantity - 1)}
                                            aria-label={item.quantity === 1 ? "Remove item" : "Decrease quantity"}
                                            title={item.quantity === 1 ? "Remove item" : "Decrease quantity"}
                                            style={
                                                item.quantity === 1
                                                    ? { color: "#dc3545" }
                                                    : undefined
                                            }
                                        >
                                            {item.quantity === 1 ? (
                                                <Trash2 size={12} strokeWidth={2} />
                                            ) : (
                                                <Minus size={12} strokeWidth={2} />
                                            )}
                                        </button>

                                        <span>{item.quantity}</span>

                                        <button
                                            type="button"
                                            onClick={() => handleQuantityUpdate(item.id, item.quantity + 1)}
                                            aria-label="Increase quantity"
                                        >
                                            <Plus size={12} strokeWidth={2} />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {hasPhysicalItem && (
                                <div className="checkout-review-shipping">
                                    <h3>Shipping</h3>

                                    {!selectedAddress && (
                                        <p style={{ color: "#667085", fontSize: "12px", margin: "6px 0" }}>
                                            Select a delivery address to see shipping options.
                                        </p>
                                    )}

                                    {selectedAddress && pdpDeliveryLoading && (
                                        <p style={{ color: "#667085", fontSize: "12px", margin: "6px 0" }}>
                                            Checking delivery to {selectedAddress.pincode}…
                                        </p>
                                    )}

                                    {selectedAddress && undeliverable && (
                                        <UndeliverableBanner pincode={selectedAddress.pincode} />
                                    )}

                                    {selectedAddress && !pdpDeliveryLoading && !undeliverable && (
                                        <>
                                            {/* Standard Delivery Option */}
                                            <label
                                                className={`checkout-shipping-option ${
                                                    currentOptionId === "std" ? "active" : ""
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name={`delivery-${item.id}`}
                                                    checked={currentOptionId === "std"}
                                                    onChange={() =>
                                                        handleSelectDelivery(
                                                            "Standard Delivery",
                                                            Number(standardOpt?.price ?? 0),
                                                            "std",
                                                            undefined,
                                                            standardEstimatedDays
                                                        )
                                                    }
                                                />
                                                <span>
                                                    <strong>
                                                        Standard Delivery (
                                                        {Number(standardOpt?.price ?? 0) > 0
                                                            ? `₹${standardOpt?.price}`
                                                            : "FREE"}
                                                        )
                                                    </strong>
                                                    <small>
                                                        {standardOpt?.etaText ||
                                                            `${standardEstimatedDays} Business Days`}
                                                    </small>
                                                </span>
                                            </label>

                                            {/* Same-day / Hyperlocal Delivery Option */}
                                            {sameDayAvailable && (
                                                <label
                                                    className={`checkout-shipping-option ${
                                                        currentOptionId === "sameday" ? "active" : ""
                                                    }`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name={`delivery-${item.id}`}
                                                        checked={currentOptionId === "sameday"}
                                                        onChange={() =>
                                                            handleSelectDelivery(
                                                                "Same-day Delivery",
                                                                Number(sameDayCost),
                                                                "sameday",
                                                                hlCheapest?.courierCompanyId,
                                                                0
                                                            )
                                                        }
                                                    />
                                                    <span>
                                                        <strong>
                                                            Same-day Delivery (₹{sameDayCost})
                                                        </strong>
                                                        <small>
                                                            {sameDayOpt?.etaText || "Delivered today"}
                                                        </small>
                                                    </span>
                                                </label>
                                            )}

                                            {/* More delivery couriers (if available) */}
                                            {hyperlocalCourierOptions.length > 1 && (
                                                <div className="checkout-courier-field">
                                                    <label htmlFor={`courier-${item.id}`}>
                                                        More Courier Partners
                                                    </label>

                                                    <div className="checkout-courier-select-wrap checkout-courier-desktop-select">
                                                        <select
                                                            id={`courier-${item.id}`}
                                                            value={
                                                                cart.metadata?.deliveryCourierCompanyId
                                                                    ? String(cart.metadata.deliveryCourierCompanyId)
                                                                    : ""
                                                            }
                                                            onChange={(e) => {
                                                                const chosen = hyperlocalCourierOptions.find(
                                                                    (o) => String(o.courierCompanyId) === e.target.value
                                                                );
                                                                if (chosen) {
                                                                    handleSelectDelivery(
                                                                        `Same-day (${chosen.courierName})`,
                                                                        chosen.rate,
                                                                        "sameday",
                                                                        chosen.courierCompanyId,
                                                                        0
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            <option value="">
                                                                Select Courier ({hyperlocalCourierOptions.length} available)
                                                            </option>
                                                            {hyperlocalCourierOptions.map((opt) => (
                                                                <option
                                                                    key={opt.courierCompanyId}
                                                                    value={opt.courierCompanyId}
                                                                >
                                                                    {opt.courierName} — ₹{opt.rate}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <ChevronDown size={14} strokeWidth={1.8} />
                                                    </div>

                                                    <div className="checkout-courier-mobile-select">
                                                        <button
                                                            type="button"
                                                            className={`checkout-courier-mobile-trigger ${
                                                                openCourierCartId === item.id ? "open" : ""
                                                            }`}
                                                            onClick={() =>
                                                                setOpenCourierCartId((curr) =>
                                                                    curr === item.id ? null : item.id
                                                                )
                                                            }
                                                            aria-haspopup="listbox"
                                                            aria-expanded={openCourierCartId === item.id}
                                                        >
                                                            <span>
                                                                {hyperlocalCourierOptions.find(
                                                                    (o) =>
                                                                        String(o.courierCompanyId) ===
                                                                        String(cart.metadata?.deliveryCourierCompanyId)
                                                                )?.courierName || "Select Courier"}
                                                            </span>
                                                            {openCourierCartId === item.id ? (
                                                                <ChevronUp size={16} strokeWidth={1.9} />
                                                            ) : (
                                                                <ChevronDown size={16} strokeWidth={1.9} />
                                                            )}
                                                        </button>

                                                        {openCourierCartId === item.id && (
                                                            <div
                                                                className="checkout-courier-mobile-menu"
                                                                role="listbox"
                                                                aria-label="Select courier"
                                                            >
                                                                {hyperlocalCourierOptions.map((opt) => {
                                                                    const isSelected =
                                                                        String(cart.metadata?.deliveryCourierCompanyId) ===
                                                                        String(opt.courierCompanyId);
                                                                    return (
                                                                        <button
                                                                            type="button"
                                                                            key={opt.courierCompanyId}
                                                                            className={isSelected ? "active" : ""}
                                                                            onClick={() => {
                                                                                handleSelectDelivery(
                                                                                    `Same-day (${opt.courierName})`,
                                                                                    opt.rate,
                                                                                    "sameday",
                                                                                    opt.courierCompanyId,
                                                                                    0
                                                                                );
                                                                                setOpenCourierCartId(null);
                                                                            }}
                                                                            role="option"
                                                                            aria-selected={isSelected}
                                                                        >
                                                                            <span>
                                                                                {opt.courierName} — ₹{opt.rate}
                                                                            </span>
                                                                            {isSelected && (
                                                                                <Check size={15} strokeWidth={2.2} />
                                                                            )}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </article>
                    );
                })}

                {(cart.voucherAppliedAmount || cart.salarCode || cart.couponCode) && (
                    <article className="checkout-review-voucher-card">
                        <div className="checkout-review-voucher-image-wrap">
                            <img
                                src="/assets/img/salarlogo.png"
                                alt="Salar Benefit"
                                className="checkout-review-voucher-image"
                            />
                        </div>

                        <div className="checkout-review-voucher-content">
                            <span className="checkout-review-voucher-label">
                                SALAR BENEFIT
                            </span>

                            <h3>
                                Salar Benefits & Savings applied to this order
                            </h3>

                            <p>
                                <strong>Platform:</strong> Salar Retail
                            </p>

                            <div className="checkout-review-applied-list">
                                {cart.salarCode && (
                                    <span>
                                        Salar Code: <strong>{cart.salarCode}</strong> <em>Applied</em>
                                    </span>
                                )}
                                {cart.couponCode && (
                                    <span>
                                        Coupon: <strong>{cart.couponCode}</strong> <em>Applied</em>
                                    </span>
                                )}
                                {Number(cart.voucherAppliedAmount ?? 0) > 0 && (
                                    <span>
                                        Shopping Voucher: <strong>₹{cart.voucherAppliedAmount}</strong> <em>Applied</em>
                                    </span>
                                )}
                            </div>
                        </div>
                    </article>
                )}
            </div>
        </section>
    );
};

export default CheckoutReviewOrder;
